import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface POLineItem {
  id: string;
  inventory_item_id: string | null;
  item_name: string;
  item_code: string;
  uom: string;
  /** Raw uom stored on the PO line (no display fallback) — used for the pack-size conversion check. */
  po_uom: string | null;
  /** Dispense/stock unit from the catalog (inventory_items.unit). */
  dispense_unit: string | null;
  /** Order unit from the catalog (inventory_items.order_unit). */
  order_unit: string | null;
  /** Dispense units per order unit (inventory_items.pack_size). */
  pack_size: number | null;
  /** Current warehouse_on_hand for the catalog item (dispense units). */
  warehouse_on_hand: number | null;
  /**
   * Catalog flag: order_unit differs from dispense unit but pack_size is still 1
   * (unverified). Receiving such a line is blocked server-side; we mirror that
   * here so the user gets a clear message before submitting.
   */
  receive_blocked: boolean;
  qty_to_order: number;
  qty_received: number;
  unit_price: number;
}

const normalizeUnit = (value: string | null | undefined) => (value || '').trim().toLowerCase();

/**
 * Pack-size conversion applies ONLY when the PO line was ordered in the
 * catalog's order unit (e.g. "box") and that differs from the dispense unit
 * (e.g. "pcs"). Lines ordered directly in the dispense unit — or with no
 * explicit uom on the PO line — are received 1:1 into stock.
 */
const getStockMultiplier = (item: POLineItem): number => {
  const packSize = item.pack_size ?? 0;
  if (packSize <= 1) return 1;
  if (!item.po_uom || !item.order_unit) return 1;
  const poUom = normalizeUnit(item.po_uom);
  if (poUom !== normalizeUnit(item.order_unit)) return 1;
  if (poUom === normalizeUnit(item.dispense_unit)) return 1;
  return packSize;
};

interface GoodsReceivingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrderId: string | null;
  poNumber: string;
  supplierName: string;
  userId: string;
  onReceived?: () => void;
}

const GoodsReceivingDialog: React.FC<GoodsReceivingDialogProps> = ({
  open, onOpenChange, purchaseOrderId, poNumber, supplierName, userId, onReceived,
}) => {
  const { toast } = useToast();
  const [items, setItems] = useState<POLineItem[]>([]);
  const [receiveQtys, setReceiveQtys] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // One stable idempotency key per receipt attempt. Generated when the dialog
  // opens and reused across retries so a double-submit (network/double-click)
  // is deduped server-side; rotated after a successful receipt.
  const idempotencyKeyRef = useRef<string>('');

  useEffect(() => {
    if (open) {
      idempotencyKeyRef.current =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : '';
    }
  }, [open]);

  useEffect(() => {
    if (!open || !purchaseOrderId) return;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('inventory_purchase_order_items')
        .select('id, inventory_item_id, qty_to_order, qty_received, unit_cost_snapshot, unit_price, uom, item_description, inventory_items(name, given_id, unit, order_unit, pack_size, warehouse_on_hand, receive_blocked)')
        .eq('purchase_order_id', purchaseOrderId) as any;

      const mapped: POLineItem[] = (data || []).map((r: any) => ({
        id: r.id,
        inventory_item_id: r.inventory_item_id,
        item_name: r.inventory_items?.name || r.item_description || 'Unknown',
        item_code: r.inventory_items?.given_id || '',
        uom: r.uom || r.inventory_items?.unit || '',
        po_uom: r.uom || null,
        dispense_unit: r.inventory_items?.unit || null,
        order_unit: r.inventory_items?.order_unit || null,
        pack_size: r.inventory_items?.pack_size != null ? Number(r.inventory_items.pack_size) : null,
        warehouse_on_hand: r.inventory_items?.warehouse_on_hand != null ? Number(r.inventory_items.warehouse_on_hand) : null,
        receive_blocked: Boolean(r.inventory_items?.receive_blocked),
        qty_to_order: r.qty_to_order,
        qty_received: (r as any).qty_received || 0,
        unit_price: r.unit_cost_snapshot || r.unit_price || 0,
      }));
      setItems(mapped);
      const defaults: Record<string, string> = {};
      mapped.forEach((item) => {
        const remaining = item.qty_to_order - item.qty_received;
        defaults[item.id] = remaining > 0 ? String(remaining) : '0';
      });
      setReceiveQtys(defaults);
      setLoading(false);
    })();
  }, [open, purchaseOrderId]);

  /**
   * A line's entered quantity is valid when it's empty/zero (skipped) or a
   * finite positive number no greater than what's still outstanding. Anything
   * else (NaN, negative, over-receipt) blocks submission — and the RPC
   * re-validates server-side regardless.
   */
  const isLineValid = (item: POLineItem): boolean => {
    const raw = (receiveQtys[item.id] || '').trim();
    if (raw === '') return true;
    const qty = Number(raw);
    if (qty === 0) return true;
    // A blocked line (unverified pack size) can never receive a positive qty;
    // the server would RAISE. Surface it as invalid before submission.
    if (item.receive_blocked) return false;
    const remaining = item.qty_to_order - item.qty_received;
    return Number.isFinite(qty) && qty > 0 && qty <= remaining;
  };

  const allLinesValid = items.every(isLineValid);

  const handleReceive = async () => {
    if (!purchaseOrderId || !allLinesValid) return;

    const receipts = items
      .map((item) => ({ item_id: item.id, qty: Number((receiveQtys[item.id] || '').trim() || 0) }))
      .filter((r) => Number.isFinite(r.qty) && r.qty > 0);

    if (receipts.length === 0) {
      toast({ title: 'Nothing to receive', description: 'Enter quantities first.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);

    // Single atomic, server-validated RPC: locks the PO + lines, validates
    // each quantity (finite, positive, within remaining), applies the
    // pack-size conversion in SQL, writes the receiving log, and updates the
    // PO status — all or nothing.
    const { data, error } = await supabase.rpc('receive_purchase_order_items' as any, {
      p_po_id: purchaseOrderId,
      p_user_id: userId,
      p_receipts: receipts,
      ...(idempotencyKeyRef.current ? { p_request_id: idempotencyKeyRef.current } : {}),
    } as any) as any;

    if (error) {
      toast({
        title: 'Receiving failed',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      const newStatus: string = data?.new_status || 'partially_received';
      toast({
        title: 'Goods received',
        description: `PO ${poNumber} marked as ${newStatus.replace('_', ' ')}. Stock updated.`,
      });
      // Rotate the idempotency key so a later, distinct receipt isn't deduped.
      idempotencyKeyRef.current =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : '';
      onReceived?.();
      onOpenChange(false);
    }
    setSubmitting(false);
  };

  const handleReceiveAllInFull = () => {
    setReceiveQtys(() => {
      const next: Record<string, string> = {};
      items.forEach((item) => {
        const remaining = item.qty_to_order - item.qty_received;
        next[item.id] = remaining > 0 ? String(remaining) : '0';
      });
      return next;
    });
  };

  const totalToReceive = items.reduce((sum, item) => {
    const qty = Number((receiveQtys[item.id] || '').trim() || 0);
    return Number.isFinite(qty) && qty > 0 ? sum + qty : sum;
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Receive Goods — {poNumber}</DialogTitle>
          <DialogDescription>Supplier: {supplierName}. Enter quantities received. Stock will be added to warehouse.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading PO items...</p>
        ) : (
          <>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReceiveAllInFull}
              disabled={items.every((item) => item.qty_to_order - item.qty_received <= 0)}
            >
              Receive all in full
            </Button>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Ordered</TableHead>
                  <TableHead className="text-right">Already received</TableHead>
                  <TableHead className="text-right">Receive now</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const remaining = item.qty_to_order - item.qty_received;
                  const stockMultiplier = getStockMultiplier(item);
                  const receiveQty = Number(receiveQtys[item.id] || 0);
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <p className="font-medium text-slate-900">{item.item_name}</p>
                        <p className="text-xs text-slate-500">{item.item_code} · {item.uom} · ${item.unit_price.toFixed(2)}</p>
                      </TableCell>
                      <TableCell className="text-right">{item.qty_to_order}</TableCell>
                      <TableCell className="text-right">
                        {item.qty_received > 0 ? (
                          <Badge variant="outline" className="border-green-300 text-green-700">{item.qty_received}</Badge>
                        ) : '0'}
                      </TableCell>
                      <TableCell className="text-right">
                        {remaining > 0 ? (
                          <>
                            <Input
                              type="number"
                              min={0}
                              max={remaining}
                              value={receiveQtys[item.id] || ''}
                              onChange={(e) => setReceiveQtys((prev) => ({ ...prev, [item.id]: e.target.value }))}
                              className="w-20 text-right ml-auto"
                              disabled={item.receive_blocked}
                            />
                            {item.receive_blocked ? (
                              <p className="mt-1 text-[11px] text-red-600">
                                Pack size unverified ({item.order_unit} vs {item.dispense_unit}). Set the
                                correct pack size on the catalog item before receiving.
                              </p>
                            ) : !isLineValid(item) ? (
                              <p className="mt-1 text-[11px] text-red-600">
                                Max {remaining} remaining
                              </p>
                            ) : receiveQty > 0 ? (
                              <p className="mt-1 text-[11px] text-slate-500">
                                {stockMultiplier > 1
                                  ? `${receiveQty} ${item.order_unit || item.uom} = ${receiveQty * stockMultiplier} ${item.dispense_unit || 'units'}`
                                  : `${receiveQty} ${item.dispense_unit || item.uom || 'units'}`}
                                {item.warehouse_on_hand != null
                                  ? ` · on-hand ${item.warehouse_on_hand} → ${item.warehouse_on_hand + receiveQty * stockMultiplier}`
                                  : ''}
                              </p>
                            ) : null}
                          </>
                        ) : (
                          <Badge variant="outline" className="border-green-300 text-green-700">Complete</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={handleReceive} disabled={submitting || totalToReceive === 0 || !allLinesValid}>
            {submitting
              ? 'Recording...'
              : `Record receipt (${totalToReceive} ${totalToReceive === 1 ? 'unit' : 'units'})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GoodsReceivingDialog;
